import { useState } from 'react';
import { useTranslations } from 'use-intl';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  AlertDialog,
  Body,
  Button,
  Card,
  Eyebrow,
  PageHeader,
  Row,
  Stack,
  useDelayedLoading,
} from '@leanin/ui';
import { groupKeys } from '../../../lib/queries/groups';
import { spacesKeys } from '../../../lib/queries/spaces';
import {
  useArchiveGroup,
  useRestoreGroup,
} from '../../../lib/mutations/groups';
import { AdminFormPageSkeleton } from './AdminFormPageSkeleton';
import { errorMessage } from '../../../lib/error-message';

type GroupKind = 'circle' | 'network' | 'space';

type Props = {
  readonly groupId: string;
  readonly kind: GroupKind;
};

const POST_ARCHIVE_ROUTE: Record<
  GroupKind,
  '/circles/$id' | '/networks/$id' | '/groups/$id'
> = {
  circle: '/circles/$id',
  network: '/networks/$id',
  space: '/groups/$id',
};

type Loaded = {
  readonly groupName: string;
  readonly status: 'active' | 'archived';
};

export function GroupArchiveSection({ groupId, kind }: Props) {
  // Parallel queries, each gated by `kind` via `enabled`. The inactive
  // branches are no-ops; the active one populates `loaded` below.
  const circleQuery = useQuery({
    ...groupKeys.circle.detail(groupId),
    enabled: kind === 'circle',
  });
  const networkQuery = useQuery({
    ...groupKeys.network.detail(groupId),
    enabled: kind === 'network',
  });
  const spaceQuery = useQuery({
    ...spacesKeys.detail(groupId),
    enabled: kind === 'space',
  });

  let loaded: Loaded | null = null;
  if (kind === 'circle') {
    const detail = circleQuery.data;
    if (detail !== undefined && detail.access !== 'visitor') {
      loaded = { groupName: detail.circle.name, status: detail.circle.status };
    }
  } else if (kind === 'network') {
    const detail = networkQuery.data;
    if (detail !== undefined) {
      loaded = { groupName: detail.name, status: detail.status };
    }
  } else {
    const detail = spaceQuery.data;
    if (detail !== undefined && detail !== null) {
      loaded = {
        groupName: detail.name,
        status: detail.archivedAt !== null ? 'archived' : 'active',
      };
    }
  }

  const showSkeleton = useDelayedLoading(loaded === null);
  if (showSkeleton) {
    return <AdminFormPageSkeleton cards={1} rowsPerCard={4} withAction />;
  }
  if (loaded === null) return null;

  return loaded.status === 'archived' ? (
    <RestorePanel groupId={groupId} groupName={loaded.groupName} kind={kind} />
  ) : (
    <ArchivePanel groupId={groupId} groupName={loaded.groupName} kind={kind} />
  );
}

function ArchivePanel({
  groupId,
  groupName,
  kind,
}: {
  groupId: string;
  groupName: string;
  kind: GroupKind;
}) {
  const t = useTranslations('Group');
  const navigate = useNavigate();
  const archiveMutation = useArchiveGroup();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleArchive = () => {
    setError(null);
    archiveMutation.mutate(groupId, {
      onSuccess: () => {
        navigate({ to: POST_ARCHIVE_ROUTE[kind], params: { id: groupId } });
      },
      onError: (err) =>
        setError(errorMessage(err, t('admin.archive.error', { kind }))),
    });
  };

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('admin.archive.title')}
        subtitle={t('admin.archive.subtitle', { name: groupName })}
      />

      <Card pad="default">
        <Stack gap="lg">
          <Eyebrow tone="accent">{t('admin.archive.whatHappensEyebrow')}</Eyebrow>
          <Stack gap="sm">
            <Body size="sm" color="mute">
              · {t('admin.archive.bullet1', { kind })}
            </Body>
            <Body size="sm" color="mute">
              · {t('admin.archive.bullet2', { kind })}
            </Body>
            <Body size="sm" color="mute">
              · {t('admin.archive.bullet3', { kind })}
            </Body>
            <Body size="sm" color="mute">
              · {t('admin.archive.bullet4')}
            </Body>
          </Stack>
          <Row>
            <Button
              variant="ghostDanger"
              size="md"
              onClick={() => setConfirmOpen(true)}
              loading={archiveMutation.isPending}
            >
              {t('admin.archive.button', { kind })}
            </Button>
          </Row>
          {error !== null ? (
            <span role="alert" aria-live="polite">
              <Body size="sm" color="destructive">{error}</Body>
            </span>
          ) : null}
        </Stack>
      </Card>

      <AlertDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('admin.archive.dialogTitle', { name: groupName })}
        description={t('admin.archive.dialogDescription', { kind })}
        actionLabel={t('admin.archive.button', { kind })}
        cancelLabel={t('admin.archive.dialogCancel', { kind })}
        destructive
        onAction={handleArchive}
      />
    </Stack>
  );
}

function RestorePanel({
  groupId,
  groupName,
  kind,
}: {
  groupId: string;
  groupName: string;
  kind: GroupKind;
}) {
  const t = useTranslations('Group');
  const restoreMutation = useRestoreGroup();
  const [error, setError] = useState<string | null>(null);

  const handleRestore = () => {
    setError(null);
    restoreMutation.mutate(groupId, {
      onError: (err) =>
        setError(errorMessage(err, t('admin.archive.restoreError', { kind }))),
    });
  };

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('admin.archive.restoreTitle')}
        subtitle={t('admin.archive.restoreSubtitle', { name: groupName, kind })}
      />

      <Card pad="default">
        <Stack gap="lg">
          <Eyebrow tone="accent">
            {t('admin.archive.restoreWhatHappensEyebrow')}
          </Eyebrow>
          <Stack gap="sm">
            <Body size="sm" color="mute">
              · {t('admin.archive.restoreBullet1', { kind })}
            </Body>
            <Body size="sm" color="mute">
              · {t('admin.archive.restoreBullet2', { kind })}
            </Body>
            <Body size="sm" color="mute">
              · {t('admin.archive.restoreBullet3')}
            </Body>
          </Stack>
          <Row>
            <Button
              variant="primary"
              size="md"
              onClick={handleRestore}
              loading={restoreMutation.isPending}
            >
              {t('admin.archive.restoreButton', { kind })}
            </Button>
          </Row>
          {error !== null ? (
            <span role="alert" aria-live="polite">
              <Body size="sm" color="destructive">{error}</Body>
            </span>
          ) : null}
        </Stack>
      </Card>
    </Stack>
  );
}
